import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import mfaService from "../../services/mfa.service";
import authService from "../../services/auth.service";
import toast from 'react-hot-toast';

// ============================================================================
// ASYNC THUNKS FOR MFA OPERATIONS
// ============================================================================

/**
 * Setup MFA for current user
 * Generates QR code and backup codes
 */
export const setupMFA = createAsyncThunk(
    'mfa/setup',
    async (_, { rejectWithValue }) => {
        try {
            console.log("Redux setupMFA called");

            const result = await mfaService.setupMFA();

            if (result.success) {
                console.log("MFA setup successful:", { hasQRCode: !!result.data.qrCodeUrl });
                return result;
            } else {
                console.error("MFA setup failed:", result.message);
                toast.error(result.message, {
                    position: "top-right",
                    duration: 4000,
                });
                return rejectWithValue(result);
            }
        } catch (error) {
            console.error("MFA setup error in Redux:", error);

            const errorMessage = error?.response?.data?.detail ||
                               error?.message ||
                               "MFA setup failed";

            toast.error(errorMessage, {
                position: "top-right",
                duration: 4000,
            });

            return rejectWithValue({
                success: false,
                message: errorMessage,
                error: error?.response?.data
            });
        }
    }
);

/**
 * Verify TOTP code during MFA setup
 */
export const verifySetup = createAsyncThunk(
    'mfa/verifySetup',
    async ({ totpCode, setupSessionId }, { rejectWithValue }) => {
        try {
            console.log("Redux verifySetup called", { setupSessionId });

            const result = await mfaService.verifySetup(totpCode, setupSessionId);

            if (result.success) {
                console.log("MFA setup verified successfully");
                toast.success(result.message || "MFA enabled successfully!", {
                    position: "top-right",
                    duration: 3000,
                });
                return result;
            } else {
                console.error("MFA setup verification failed:", result.message);
                toast.error(result.message, {
                    position: "top-right",
                    duration: 4000,
                });
                return rejectWithValue(result);
            }
        } catch (error) {
            console.error("MFA setup verification error:", error);

            const errorMessage = error?.response?.data?.detail ||
                               error?.message ||
                               "Invalid verification code";

            toast.error(errorMessage, {
                position: "top-right",
                duration: 4000,
            });

            return rejectWithValue({
                success: false,
                message: errorMessage,
                error: error?.response?.data
            });
        }
    }
);

/**
 * Verify TOTP code during login
 */
export const verifyLogin = createAsyncThunk(
    'mfa/verifyLogin',
    async ({ sessionId, mfaCode }, { rejectWithValue }) => {
        try {
            console.log("Redux verifyLogin called", { sessionId });

            const result = await mfaService.verifyLogin(sessionId, mfaCode);

            if (result.success) {
                console.log("MFA login verification successful", result.data);

                // Store authentication tokens and user info (critical fix!)
                if (result.data.access_token && result.data.refresh_token) {
                    authService.setTokens(
                        result.data.access_token,
                        result.data.refresh_token
                    );

                    if (result.data.user_info) {
                        authService.setUserInfo(result.data.user_info);
                    }

                    // Schedule token refresh
                    if (result.data.expires_in) {
                        authService.scheduleTokenRefresh(result.data.expires_in);
                    }

                    console.log("MFA tokens and user info stored successfully");
                } else {
                    console.warn("MFA verification response missing tokens", result.data);
                }

                if (result.isBackupCode) {
                    toast.success("Login successful with backup code", {
                        position: "top-right",
                        duration: 3000,
                    });
                } else {
                    toast.success("MFA verification successful", {
                        position: "top-right",
                        duration: 3000,
                    });
                }

                return result;
            } else {
                console.error("MFA login verification failed:", result.message);
                return rejectWithValue(result);
            }
        } catch (error) {
            console.error("MFA login verification error:", error);

            const errorMessage = error?.response?.data?.detail ||
                               error?.message ||
                               "Invalid verification code";

            return rejectWithValue({
                success: false,
                message: errorMessage,
                error: error?.response?.data
            });
        }
    }
);

/**
 * Disable MFA for current user
 */
export const disableMFA = createAsyncThunk(
    'mfa/disable',
    async (password, { rejectWithValue }) => {
        try {
            console.log("Redux disableMFA called");

            const result = await mfaService.disableMFA(password);

            if (result.success) {
                console.log("MFA disabled successfully");
                toast.success(result.message || "MFA has been disabled", {
                    position: "top-right",
                    duration: 3000,
                });
                return result;
            } else {
                console.error("MFA disable failed:", result.message);
                toast.error(result.message, {
                    position: "top-right",
                    duration: 4000,
                });
                return rejectWithValue(result);
            }
        } catch (error) {
            console.error("MFA disable error:", error);

            const errorMessage = error?.response?.data?.detail ||
                               error?.message ||
                               "Failed to disable MFA";

            toast.error(errorMessage, {
                position: "top-right",
                duration: 4000,
            });

            return rejectWithValue({
                success: false,
                message: errorMessage,
                error: error?.response?.data
            });
        }
    }
);

/**
 * Get MFA status for current user
 */
export const getMFAStatus = createAsyncThunk(
    'mfa/getStatus',
    async (_, { rejectWithValue }) => {
        try {
            console.log("Redux getMFAStatus called");

            const result = await mfaService.getMFAStatus();

            if (result.success) {
                console.log("MFA status retrieved:", result.data);
                return result;
            } else {
                console.error("Get MFA status failed:", result.message);
                return rejectWithValue(result);
            }
        } catch (error) {
            console.error("Get MFA status error:", error);

            const errorMessage = error?.response?.data?.detail ||
                               error?.message ||
                               "Failed to get MFA status";

            return rejectWithValue({
                success: false,
                message: errorMessage,
                error: error?.response?.data
            });
        }
    }
);

/**
 * Get backup codes (requires password)
 */
export const getBackupCodes = createAsyncThunk(
    'mfa/getBackupCodes',
    async (password, { rejectWithValue }) => {
        try {
            console.log("Redux getBackupCodes called");

            const result = await mfaService.getBackupCodes(password);

            if (result.success) {
                console.log("Backup codes retrieved:", { count: result.data.backupCodes?.length });
                return result;
            } else {
                console.error("Get backup codes failed:", result.message);
                toast.error(result.message, {
                    position: "top-right",
                    duration: 4000,
                });
                return rejectWithValue(result);
            }
        } catch (error) {
            console.error("Get backup codes error:", error);

            const errorMessage = error?.response?.data?.detail ||
                               error?.message ||
                               "Failed to get backup codes";

            toast.error(errorMessage, {
                position: "top-right",
                duration: 4000,
            });

            return rejectWithValue({
                success: false,
                message: errorMessage,
                error: error?.response?.data
            });
        }
    }
);

/**
 * Regenerate backup codes (requires password)
 */
export const regenerateBackupCodes = createAsyncThunk(
    'mfa/regenerateBackupCodes',
    async (password, { rejectWithValue }) => {
        try {
            console.log("Redux regenerateBackupCodes called");

            const result = await mfaService.regenerateBackupCodes(password);

            if (result.success) {
                console.log("Backup codes regenerated:", { count: result.data.backupCodes?.length });
                toast.success(result.message || "Backup codes regenerated successfully", {
                    position: "top-right",
                    duration: 3000,
                });
                return result;
            } else {
                console.error("Regenerate backup codes failed:", result.message);
                toast.error(result.message, {
                    position: "top-right",
                    duration: 4000,
                });
                return rejectWithValue(result);
            }
        } catch (error) {
            console.error("Regenerate backup codes error:", error);

            const errorMessage = error?.response?.data?.detail ||
                               error?.message ||
                               "Failed to regenerate backup codes";

            toast.error(errorMessage, {
                position: "top-right",
                duration: 4000,
            });

            return rejectWithValue({
                success: false,
                message: errorMessage,
                error: error?.response?.data
            });
        }
    }
);

// ============================================================================
// REDUX SLICE DEFINITION
// ============================================================================

const mfaSlice = createSlice({
    name: 'mfa',
    initialState: {
        // MFA status
        mfaEnabled: false,
        mfaMethod: null,
        mfaEnabledAt: null,

        // Setup state
        isSettingUp: false,
        setupData: {
            qrCodeUrl: null,
            secret: null,
            backupCodes: [],
            setupSessionId: null
        },

        // Backup codes
        backupCodes: [],
        usedCodes: [],

        // Login verification state
        mfaRequired: false,
        mfaVerified: false,
        verificationAttempts: 0,
        maxVerificationAttempts: 3,

        // UI state
        loading: false,
        error: null,

        // Modal state
        showSetupModal: false,
        showVerifyModal: false,
        showBackupCodesModal: false,
        showDisableModal: false,
    },
    reducers: {
        // Setup modal control
        openSetupModal: (state) => {
            state.showSetupModal = true;
            state.error = null;
        },
        closeSetupModal: (state) => {
            state.showSetupModal = false;
            state.isSettingUp = false;
            state.setupData = {
                qrCodeUrl: null,
                secret: null,
                backupCodes: [],
                setupSessionId: null
            };
            state.error = null;
        },

        // Verify modal control
        openVerifyModal: (state) => {
            state.showVerifyModal = true;
            state.mfaRequired = true;
            state.error = null;
        },
        closeVerifyModal: (state) => {
            state.showVerifyModal = false;
            state.error = null;
        },

        // Backup codes modal control
        openBackupCodesModal: (state) => {
            state.showBackupCodesModal = true;
            state.error = null;
        },
        closeBackupCodesModal: (state) => {
            state.showBackupCodesModal = false;
            state.backupCodes = [];
            state.usedCodes = [];
            state.error = null;
        },

        // Disable modal control
        openDisableModal: (state) => {
            state.showDisableModal = true;
            state.error = null;
        },
        closeDisableModal: (state) => {
            state.showDisableModal = false;
            state.error = null;
        },

        // Set MFA required (called during login)
        setMFARequired: (state, action) => {
            state.mfaRequired = action.payload;
        },

        // Set MFA verified (called after successful verification)
        setMFAVerified: (state, action) => {
            state.mfaVerified = action.payload;
            if (action.payload) {
                state.mfaRequired = false;
                state.verificationAttempts = 0;
            }
        },

        // Increment verification attempts
        incrementVerificationAttempts: (state) => {
            state.verificationAttempts += 1;
        },

        // Reset verification state
        resetVerificationState: (state) => {
            state.mfaRequired = false;
            state.mfaVerified = false;
            state.verificationAttempts = 0;
            state.error = null;
        },

        // Clear error
        clearError: (state) => {
            state.error = null;
        },

        // Clear all MFA state (on logout)
        clearMFAState: (state) => {
            state.mfaEnabled = false;
            state.mfaMethod = null;
            state.mfaEnabledAt = null;
            state.isSettingUp = false;
            state.setupData = {
                qrCodeUrl: null,
                secret: null,
                backupCodes: [],
                setupSessionId: null
            };
            state.backupCodes = [];
            state.usedCodes = [];
            state.mfaRequired = false;
            state.mfaVerified = false;
            state.verificationAttempts = 0;
            state.loading = false;
            state.error = null;
            state.showSetupModal = false;
            state.showVerifyModal = false;
            state.showBackupCodesModal = false;
            state.showDisableModal = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // Setup MFA
            .addCase(setupMFA.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.isSettingUp = true;
            })
            .addCase(setupMFA.fulfilled, (state, action) => {
                state.loading = false;
                state.setupData = {
                    qrCodeUrl: action.payload.data.qrCodeUrl,
                    secret: action.payload.data.secret,
                    setupSessionId: action.payload.data.setupSessionId
                    // Note: backupCodes are generated and returned only on verification
                };
                state.error = null;
            })
            .addCase(setupMFA.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || action.error.message;
                state.isSettingUp = false;
            })

            // Verify Setup
            .addCase(verifySetup.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(verifySetup.fulfilled, (state, action) => {
                state.loading = false;
                state.mfaEnabled = true;
                state.mfaMethod = 'totp';
                state.mfaEnabledAt = new Date().toISOString();
                state.isSettingUp = false;
                // Keep modal open to show backup codes
                // state.showSetupModal = false;
                // Extract backup codes from backend response (backend uses snake_case: backup_codes)
                const backupCodes = action.payload.data?.backup_codes || action.payload.data?.backupCodes || [];
                state.setupData.backupCodes = backupCodes;
                console.log('Backup codes received:', backupCodes.length, 'codes');
                state.error = null;
            })
            .addCase(verifySetup.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || action.error.message;
            })

            // Verify Login
            .addCase(verifyLogin.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(verifyLogin.fulfilled, (state, action) => {
                state.loading = false;
                state.mfaVerified = true;
                state.mfaRequired = false;
                state.verificationAttempts = 0;
                state.showVerifyModal = false;
                state.error = null;

                // Store the authentication data from response
                if (action.payload && action.payload.data) {
                    // This data is now stored in localStorage via authService
                    // We just track the verification state here
                    console.log("MFA verification state updated in Redux");
                }
            })
            .addCase(verifyLogin.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || action.error.message;
                state.verificationAttempts += 1;
            })

            // Disable MFA
            .addCase(disableMFA.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(disableMFA.fulfilled, (state, action) => {
                state.loading = false;
                state.mfaEnabled = false;
                state.mfaMethod = null;
                state.mfaEnabledAt = null;
                state.setupData = {
                    qrCodeUrl: null,
                    secret: null,
                    backupCodes: [],
                    setupSessionId: null
                };
                state.showDisableModal = false;
                state.error = null;
            })
            .addCase(disableMFA.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || action.error.message;
            })

            // Get MFA Status
            .addCase(getMFAStatus.pending, (state) => {
                state.loading = true;
            })
            .addCase(getMFAStatus.fulfilled, (state, action) => {
                state.loading = false;
                state.mfaEnabled = action.payload.data.mfaEnabled;
                state.mfaMethod = action.payload.data.mfaMethod;
                state.mfaEnabledAt = action.payload.data.mfaEnabledAt;
                state.error = null;
            })
            .addCase(getMFAStatus.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || action.error.message;
            })

            // Get Backup Codes
            .addCase(getBackupCodes.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getBackupCodes.fulfilled, (state, action) => {
                state.loading = false;
                state.backupCodes = action.payload.data.backupCodes;
                state.usedCodes = action.payload.data.usedCodes || [];
                state.error = null;
            })
            .addCase(getBackupCodes.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || action.error.message;
            })

            // Regenerate Backup Codes
            .addCase(regenerateBackupCodes.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(regenerateBackupCodes.fulfilled, (state, action) => {
                state.loading = false;
                state.backupCodes = action.payload.data.backupCodes;
                state.usedCodes = [];
                state.error = null;
            })
            .addCase(regenerateBackupCodes.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || action.error.message;
            });
    },
});

// Export actions
export const {
    openSetupModal,
    closeSetupModal,
    openVerifyModal,
    closeVerifyModal,
    openBackupCodesModal,
    closeBackupCodesModal,
    openDisableModal,
    closeDisableModal,
    setMFARequired,
    setMFAVerified,
    incrementVerificationAttempts,
    resetVerificationState,
    clearError,
    clearMFAState,
} = mfaSlice.actions;

// Export selectors
export const selectMFA = (state) => state.mfa;
export const selectMFAEnabled = (state) => state.mfa.mfaEnabled;
export const selectMFAMethod = (state) => state.mfa.mfaMethod;
export const selectMFAEnabledAt = (state) => state.mfa.mfaEnabledAt;
export const selectMFALoading = (state) => state.mfa.loading;
export const selectMFAError = (state) => state.mfa.error;
export const selectIsSettingUp = (state) => state.mfa.isSettingUp;
export const selectSetupData = (state) => state.mfa.setupData;
export const selectBackupCodes = (state) => state.mfa.backupCodes;
export const selectUsedCodes = (state) => state.mfa.usedCodes;
export const selectMFARequired = (state) => state.mfa.mfaRequired;
export const selectMFAVerified = (state) => state.mfa.mfaVerified;
export const selectVerificationAttempts = (state) => state.mfa.verificationAttempts;
export const selectMaxVerificationAttempts = (state) => state.mfa.maxVerificationAttempts;
export const selectShowSetupModal = (state) => state.mfa.showSetupModal;
export const selectShowVerifyModal = (state) => state.mfa.showVerifyModal;
export const selectShowBackupCodesModal = (state) => state.mfa.showBackupCodesModal;
export const selectShowDisableModal = (state) => state.mfa.showDisableModal;

// Computed selectors
export const selectCanRetryVerification = (state) => {
    return state.mfa.verificationAttempts < state.mfa.maxVerificationAttempts;
};

export const selectRemainingAttempts = (state) => {
    return Math.max(0, state.mfa.maxVerificationAttempts - state.mfa.verificationAttempts);
};

export const selectHasBackupCodes = (state) => {
    return state.mfa.backupCodes && state.mfa.backupCodes.length > 0;
};

export const selectUnusedBackupCodesCount = (state) => {
    const totalCodes = state.mfa.backupCodes?.length || 0;
    const usedCodesCount = state.mfa.usedCodes?.length || 0;
    return Math.max(0, totalCodes - usedCodesCount);
};

// Export reducer
export default mfaSlice.reducer;
